import { useEffect, useState, useRef } from 'react';
import Header from './Header';
import { replaceUrlParameters } from '../lib/config';
import { useUser } from '../hooks/useUser';
import { HEADER_TYPES } from '../constants/headerTypes';
import { postMessageHandler, POST_MESSAGE_ACTIONS } from '../utils/postMessageHandler';
import log from '../utils/logger';

interface FullscreenIframeProps {
    gameUrl: string | null;
    isOpen: boolean;
    onClose: () => void;
    gameName?: string;
    lobbyUrl?: string;
}

const FullscreenIframe = ({ gameUrl, isOpen, onClose, gameName, lobbyUrl }: FullscreenIframeProps) => {
    const [loadError, setLoadError] = useState(false);
    const [processedUrl, setProcessedUrl] = useState<string | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const { user } = useUser();

    const handleClose = () => {
        // Qui puoi eseguire qualsiasi logica prima di chiudere
        log.debug('🎮 Chiusura iframe:', gameName);

        // Invia postMessage all'iframe per richiedere la chiusura della sessione
        // (l'accesso diretto cross-origin non è permesso dal browser)
        postMessageHandler.send(POST_MESSAGE_ACTIONS.CLOSE_GAME_SESSION, {
            gameName,
            timestamp: new Date().toISOString()
        });

        // Invia messaggio customclose nel formato richiesto dal provider
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                `{ "source":"Wrapper", "name": "${POST_MESSAGE_ACTIONS.CUSTOM_CLOSE}" }`,
                '*'
            );
        }

        // Pulisci l'iframe per liberare memoria
        if (iframeRef.current) {
            iframeRef.current.src = 'about:blank';
        }

        // Chiamare la funzione onClose originale
        onClose();
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setLoadError(false);

            if (gameUrl) {
                const newProcessedUrl = replaceUrlParameters(gameUrl, user);
                log.debug('🎮 ✅ URL INIZIALE:', gameUrl);
                log.debug('🎮 ✅ URL PROCESSATA:', newProcessedUrl);

                setProcessedUrl(newProcessedUrl);
            }
            
            // Previeni il reload della pagina parent
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                if (iframeRef.current) {
                    e.preventDefault();
                    return '';
                }
            };
            
            window.addEventListener('beforeunload', handleBeforeUnload);
            
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                document.body.style.overflow = 'unset';
            };
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, gameUrl, user]);

    // Setup postMessage handler
    useEffect(() => {
        // Imposta il riferimento all'iframe per poter inviare messaggi
        postMessageHandler.setIframeRef(iframeRef as React.RefObject<HTMLIFrameElement>);
        
        return () => {
            // Cleanup quando il componente viene smontato
            postMessageHandler.setIframeRef(null);
        };
    }, []);


    if (!isOpen || !gameUrl) return null;

    const handleIframeLoad = () => {
        log.debug('🎮 Iframe caricato');

        // Controlla se l'iframe ha navigato alla lobby URL
        if (lobbyUrl && iframeRef.current?.contentWindow?.location) {
            try {
                const currentUrl = iframeRef.current.contentWindow.location.href;
                log.debug('🎮 URL iframe dopo caricamento:', currentUrl);

                // Normalizza le URL per confronto (rimuove trailing slash, parametri, ecc)
                const normalizeUrl = (url: string): string => {
                    try {
                        const urlObj = new URL(url);
                        return urlObj.origin + urlObj.pathname.replace(/\/$/, '');
                    } catch {
                        return url.replace(/\/$/, '');
                    }
                };

                const normalizedCurrentUrl = normalizeUrl(currentUrl);
                const normalizedLobbyUrl = normalizeUrl(lobbyUrl);

                // Chiudi se l'URL corrisponde alla lobby
                if (normalizedCurrentUrl === normalizedLobbyUrl ||
                    currentUrl.startsWith(lobbyUrl)) {
                    log.debug('🎮 ✅ Rilevata navigazione alla lobby, chiusura automatica');
                    handleClose();
                }
            } catch (error) {
                log.warn('🎮 Impossibile leggere URL iframe:', error);
            }
        }
    };

    const handleIframeError = () => {
        setLoadError(true);
        log.error('🎮 ❌ ERRORE CARICAMENTO IFRAME:', {
            url: gameUrl,
            message: 'Possibile blocco da content blocker o CORS policy'
        });
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black">
            <Header onClose={handleClose} type={HEADER_TYPES.CLOSE_ONLY}/>

            {/* Game container */}
            <div className="relative" style={{ height: 'calc(100vh - 55px)' }}>

                {/* Error overlay */}
                {loadError && (
                    <div className="absolute inset-0 bg-black flex items-center justify-center z-10">
                        <div className="text-center max-w-md">
                            <div className="text-red-500 text-6xl mb-4">⚠️</div>
                            <h3 className="text-white text-xl font-bold mb-2">
                                Errore nel caricamento
                            </h3>
                            <p className="text-gray-400 mb-6">
                                Non è stato possibile caricare il contenuto. Verifica la tua connessione e riprova.
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-colors"
                                >
                                    Ricarica
                                </button>
                                <button
                                    onClick={handleClose}
                                    className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded transition-colors"
                                >
                                    Chiudi
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Game iframe */}
                <iframe
                    ref={iframeRef}
                    src={processedUrl || gameUrl}
                    className="w-full h-full border-0"
                    title={`Gioco ${gameName || 'SNAI'}`}
                    allow="autoplay; encrypted-media"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
                    referrerPolicy="strict-origin-when-cross-origin"
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                />
                
            </div>
        </div>
    );
};

export default FullscreenIframe;